/* eslint-disable ember/no-computed-properties-in-native-classes */
import { computed } from '@ember/object';
import { dependentKeyCompat } from '@ember/object/compat';
import Service, { type Registry as Services, service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import type { Hotelinfo } from 'frontend-utils/models';
import type { Rsbaserate } from 'lumen/models';

export default class RsSelectionsService extends Service {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];

  @tracked descendingBaseBaseRates: Rsbaserate[] = [];
  @tracked referencePeriodCancellationPolicySection = 0;
  @tracked referencePeriodStrategySection = 0;
  @tracked selectedCompetitorIDs: string[] = [];

  baseBaseRatesPerHotelInfo = new Map();

  @computed('filteredBaseBaseRates.length')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get compsetSize() {
    return this.filteredBaseBaseRates?.length;
  }

  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get compsetString() {
    const totalNumberOfOptions =
      this.hotelAndMarketService.primaryAndSecondaryCompetitors.length;
    const numberOfSelectedOptions = this.selectedCompetitorIDs.length;

    // If one item is selected, display the name
    if (numberOfSelectedOptions === 1) {
      const selectedCompetitor =
        this.hotelAndMarketService.primaryAndSecondaryCompetitors.find(
          (hotelinfo) => hotelinfo.id === this.selectedCompetitorIDs[0],
        );
      return selectedCompetitor?.name;
    }

    // If all options are selected, display "All ..."
    if (numberOfSelectedOptions === totalNumberOfOptions) {
      return this.intl.t('general.competitors.count.any');
    }

    // Otherwise, simply show the number of selected competitors
    return this.intl.t('general.competitors.count', {
      count: numberOfSelectedOptions,
    });
  }

  // Necessary to observe selectedCompetitorIDs
  @cached
  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get filteredBaseBaseRates() {
    // Filter out general soldouts and general missings for the positions
    return this.descendingBaseBaseRates
      .filter((baseRate) => !baseRate.hasNoBaseRate)
      .filter(
        (baseRate) =>
          this.hotelAndMarketService.isOwnHotelInfo(baseRate.hotelinfo) ||
          // @ts-expect-error:  Argument of type 'string | null' is not assignable to parameter of type 'string'.
          this.selectedCompetitorIDs.includes(baseRate.hotelinfo.id),
      );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get filteredCompetitors() {
    return this.hotelAndMarketService.primaryAndSecondaryCompetitors.filter(
      (competitor) =>
        // @ts-expect-error:  Argument of type 'string | null' is not assignable to parameter of type 'string'.
        this.selectedCompetitorIDs.includes(competitor.id),
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get ownHotelinfoId() {
    return this.hotelAndMarketService.hotel?.hotelinfo.id;
  }

  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get shouldRedirectToSecondaryCompset() {
    // See https://docs.google.com/document/d/1MWf0Bp7LQf40uPrje-WaGocvq6dBKxKgJ9Cmw--wvN8/edit#heading=h.7vpkalu4n7lh
    const { primaryCompetitors, secondaryCompetitors } =
      this.hotelAndMarketService;

    const primaryCompetitorIDs = this.selectedCompetitorIDs.filter(
      (competitorID) =>
        primaryCompetitors
          .map((hotelinfo: Hotelinfo) => hotelinfo.id)
          .includes(competitorID),
    );
    const secondaryCompetitorIDs = this.selectedCompetitorIDs.filter(
      (competitorID) =>
        secondaryCompetitors
          .map((hotelinfo: Hotelinfo) => hotelinfo.id)
          .includes(competitorID),
    );
    const numberOfSharedCompetitors = this.selectedCompetitorIDs.filter(
      (competitorID) =>
        primaryCompetitorIDs.includes(competitorID) &&
        secondaryCompetitorIDs.includes(competitorID),
    ).length;

    const hasAtLeastOneExclusivePrimaryCompetitor =
      primaryCompetitorIDs.length > numberOfSharedCompetitors;
    const hasNoPrimaryCompetitors = primaryCompetitorIDs.length === 0;
    const hasAllSecondaryCompetitorsSelected =
      secondaryCompetitorIDs.length === secondaryCompetitors.length;

    if (hasAtLeastOneExclusivePrimaryCompetitor) {
      return false;
    }
    if (hasNoPrimaryCompetitors || hasAllSecondaryCompetitorsSelected) {
      return true;
    }

    return false;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get usualPricePoint() {
    if (!this.ownHotelinfoId) {
      return -1;
    }

    // Determine the position of the own hotelinfo in the sorted and filtered list of hotelinfo ids
    return (
      this.filteredBaseBaseRates
        .map((baseRate) => baseRate.hotelinfo.id)
        .indexOf(this.ownHotelinfoId) + 1
    );
  }

  initBaseRatesProperties(allBaseRates: Rsbaserate[]): void {
    // Clear map before populating it (in case competitors in compset have changed)
    this.baseBaseRatesPerHotelInfo.clear();
    // Filter on base rates with isBase property and sort descending
    const descendingBaseBaseRates = allBaseRates
      .filter((baseRate) => baseRate.isBase)
      .sort((baseRateA, baseRateB) => baseRateB.rate - baseRateA.rate);

    this.descendingBaseBaseRates = descendingBaseBaseRates;
    // Create a mapping of these baserates per hotelinfo, because we'll need to able to access these in this way to handle edge cases
    descendingBaseBaseRates.forEach((baseRate) => {
      this.baseBaseRatesPerHotelInfo.set(baseRate.hotelinfo.id, baseRate);
    });
  }

  setSelectedCompetitorIDs(selectedCompetitorIDs: string[]): void {
    this.selectedCompetitorIDs = selectedCompetitorIDs;
  }
}

declare module '@ember/service' {
  interface Registry {
    'rs-selections': RsSelectionsService;
  }
}
